export interface Disposable {
  dispose(): void;
}

export type DisposeFunction = () => void;

export class DisposableRegistry implements Disposable {
  private cleanups: DisposeFunction[] = [];
  private disposed = false;

  get isDisposed(): boolean {
    return this.disposed;
  }

  get size(): number {
    return this.cleanups.length;
  }

  add(resource: Disposable | DisposeFunction): DisposeFunction {
    const cleanup = typeof resource === 'function' ? resource : () => resource.dispose();
    let active = true;
    const invoke = (): void => {
      if (!active) return;
      active = false;
      cleanup();
    };
    if (this.disposed) {
      invoke();
      return () => undefined;
    }
    this.cleanups.push(invoke);
    return () => {
      const index = this.cleanups.indexOf(invoke);
      if (index >= 0) this.cleanups.splice(index, 1);
      invoke();
    };
  }

  listen<TEvent extends Event>(
    target: EventTarget,
    type: string,
    listener: (event: TEvent) => void,
    options?: AddEventListenerOptions | boolean,
  ): DisposeFunction {
    const eventListener: EventListener = (event) => listener(event as TEvent);
    target.addEventListener(type, eventListener, options);
    return this.add(() => target.removeEventListener(type, eventListener, options));
  }

  timeout(callback: () => void, delayMs: number): DisposeFunction {
    const timeout = globalThis.setTimeout(callback, delayMs);
    return this.add(() => globalThis.clearTimeout(timeout));
  }

  interval(callback: () => void, delayMs: number): DisposeFunction {
    const interval = globalThis.setInterval(callback, delayMs);
    return this.add(() => globalThis.clearInterval(interval));
  }

  child(): DisposableRegistry {
    const child = new DisposableRegistry();
    this.add(child);
    return child;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const errors: unknown[] = [];
    for (const cleanup of this.cleanups.splice(0).reverse()) {
      try {
        cleanup();
      } catch (error) {
        errors.push(error);
      }
    }
    if (errors.length > 0)
      throw new AggregateError(errors, 'One or more resources failed to dispose');
  }
}

export class EntityRegistry<T extends { active?: boolean }> {
  private readonly entities = new Set<T>();

  get size(): number {
    return this.entities.size;
  }

  add(entity: T): T {
    this.entities.add(entity);
    return entity;
  }

  delete(entity: T): boolean {
    return this.entities.delete(entity);
  }

  active(): T[] {
    return [...this.entities].filter((entity) => entity.active !== false);
  }

  sweep(): number {
    let removed = 0;
    for (const entity of this.entities) {
      if (entity.active === false) {
        this.entities.delete(entity);
        removed += 1;
      }
    }
    return removed;
  }

  clear(dispose?: (entity: T) => void): void {
    if (dispose) for (const entity of this.entities) dispose(entity);
    this.entities.clear();
  }
}
